import React, { useState } from "react";
import {
  MessageCircle,
  Plus,
  GripVertical,
  X,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

import type {
  ProgramForm as ProgramFormType,
  FAQ,
} from "@/types/program";

interface MarketingStepV2Props {
  data: Partial<ProgramFormType>;
  onUpdate: (data: Partial<ProgramFormType>) => void;
  programType?: string;
  formData?: any;
  isEditMode?: boolean;
  onFieldChange?: (fieldPath: string) => void;
}

export const MarketingStepV2: React.FC<MarketingStepV2Props> = ({
  data,
  onUpdate,
  onFieldChange,
}) => {
  const faqs = data.faqs || [];
  const DEFAULT_FAQ_TYPE: FAQ["type"] = "admissions";

  // FAQ Management
  const addFAQ = () => {
    const newFAQ: FAQ = {
      id: Date.now(),
      type: DEFAULT_FAQ_TYPE,
      question: "",
      answer: "",
    };
    onUpdate({ faqs: [newFAQ, ...faqs] });
    onFieldChange?.('faqs');
  };

  const updateFAQ = (index: number, field: keyof FAQ, value: string) => {
    const updatedFaqs = faqs.map((faq, i) =>
      i === index ? { ...faq, [field]: value } : faq
    );
    onUpdate({ faqs: updatedFaqs });
    onFieldChange?.('faqs');
  };

  const removeFAQ = (id: number) => {
    const updatedFaqs = faqs.filter(faq => faq.id !== id);
    onUpdate({ faqs: updatedFaqs });
    onFieldChange?.('faqs');
  };

  // Drag and drop handlers for FAQs
  const [draggedFAQ, setDraggedFAQ] = useState<number | null>(null);
  const [dragOverFAQ, setDragOverFAQ] = useState<number | null>(null);

  const handleFAQDragStart = (e: React.DragEvent, faqIndex: number) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ faqIndex }));
    e.dataTransfer.effectAllowed = 'move';
    setDraggedFAQ(faqIndex);
  };

  const handleFAQDragEnd = () => {
    setDraggedFAQ(null);
    setDragOverFAQ(null);
  };

  const handleFAQDragOver = (e: React.DragEvent, faqIndex: number) => {
    if (draggedFAQ === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFAQ(faqIndex);
  };

  const handleFAQDragLeave = () => {
    setDragOverFAQ(null);
  };

  const handleFAQDrop = (e: React.DragEvent, targetFAQIndex: number) => {
    if (draggedFAQ === null) return;
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    const { faqIndex: sourceFAQIndex } = data;

    if (sourceFAQIndex === targetFAQIndex) {
      return; // Same position, no change needed
    }

    const updatedFAQs = [...faqs];
    const [movedFAQ] = updatedFAQs.splice(sourceFAQIndex, 1);
    
    // Adjust target index if needed
    const adjustedTargetIndex = sourceFAQIndex < targetFAQIndex ? targetFAQIndex - 1 : targetFAQIndex;
    updatedFAQs.splice(adjustedTargetIndex, 0, movedFAQ);

    onUpdate({ faqs: updatedFAQs });
    onFieldChange?.('faqs');
    setDragOverFAQ(null);
    
    // Show success animation
    toast.success("FAQ reordered successfully!");
  };

  const moveFAQUp = (faqIndex: number) => {
    if (faqIndex === 0) return;
    
    const updatedFAQs = [...faqs];
    [updatedFAQs[faqIndex - 1], updatedFAQs[faqIndex]] = [updatedFAQs[faqIndex], updatedFAQs[faqIndex - 1]];
    
    onUpdate({ faqs: updatedFAQs });
    onFieldChange?.('faqs');
    
    // Show success animation
    toast.success("FAQ moved up!");
  };

  const moveFAQDown = (faqIndex: number) => {
    if (faqIndex === faqs.length - 1) return;
    
    const updatedFAQs = [...faqs];
    [updatedFAQs[faqIndex], updatedFAQs[faqIndex + 1]] = [updatedFAQs[faqIndex + 1], updatedFAQs[faqIndex]];
    
    onUpdate({ faqs: updatedFAQs });
    onFieldChange?.('faqs');
    
    // Show success animation
    toast.success("FAQ moved down!");
  };

  // no-op

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center gap-2">
          <MessageCircle className="h-6 w-6 text-green-600" />
          <h2 className="text-2xl font-bold text-gray-900">
            Frequently Asked Questions
          </h2>
        </div>
        <p className="text-gray-600">
          Create comprehensive FAQs to reduce support requests and improve user experience
        </p>
      </div>

      {/* FAQ Management */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              FAQ Management
            </h3>
          </div>
          <Button type="button" onClick={addFAQ} variant="outline" size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add FAQ
          </Button>
        </div>

        <div className="space-y-4">
          {faqs?.map((faq, index) => {
            const isDragged = draggedFAQ === index;
            const isDragOver = dragOverFAQ === index;
            
            return (
              <div
                key={faq.id}
                className={`
                  relative p-4 border rounded-lg transition-all duration-300 ease-in-out
                  ${isDragged 
                    ? 'opacity-50 scale-95 rotate-1 shadow-lg' 
                    : isDragOver 
                      ? 'border-purple-400 bg-purple-50 scale-105 shadow-md' 
                      : 'bg-muted/30 hover:bg-muted/50 hover:shadow-sm'
                  }
                  ${isDragOver ? 'border-dashed border-2' : 'border-solid'}
                `}
                onDragOver={(e) => handleFAQDragOver(e, index)}
                onDragLeave={handleFAQDragLeave}
                onDrop={(e) => handleFAQDrop(e, index)}
              >
                <div className="absolute top-3 left-3 flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => moveFAQUp(index)}
                    disabled={index === 0}
                    className="h-7 w-7 p-0 transition-all duration-200 hover:scale-110 disabled:opacity-30"
                    aria-label="Move up"
                    title="Move up"
                  >
                    <span className="text-sm font-bold">↑</span>
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => moveFAQDown(index)}
                    disabled={index === faqs.length - 1}
                    className="h-7 w-7 p-0 transition-all duration-200 hover:scale-110 disabled:opacity-30"
                    aria-label="Move down"
                    title="Move down"
                  >
                    <span className="text-sm font-bold">↓</span>
                  </Button>
                  <div
                    className="ml-1"
                    draggable
                    onDragStart={(e) => handleFAQDragStart(e, index)}
                    onDragEnd={handleFAQDragEnd}
                    aria-label="Drag to reorder FAQ"
                    title="Drag to reorder"
                  >
                    <GripVertical className="h-5 w-5 text-muted-foreground cursor-move transition-colors duration-200 hover:text-foreground" />
                  </div>
                </div>

                <div className="flex items-start justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      removeFAQ(faq.id);
                      toast.success("FAQ removed!");
                    }}
                    aria-label={`Remove FAQ ${index + 1}`}
                    className="transition-all duration-200 hover:scale-110 hover:bg-red-50 hover:border-red-300"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-4 pt-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor={`faq-type-${faq.id}`}>Type</Label>
                        <Select
                          value={faq.type}
                          onValueChange={(value) => updateFAQ(index, "type", value)}
                        >
                          <SelectTrigger className="transition-all duration-200 focus:ring-2 focus:ring-purple-400">
                            <SelectValue placeholder="Select FAQ type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admissions">Admissions</SelectItem>
                            <SelectItem value="payments">Payments</SelectItem>
                            <SelectItem value="program">Program</SelectItem>
                            <SelectItem value="schedule">Schedule</SelectItem>
                            <SelectItem value="certification">Certification</SelectItem>
                            <SelectItem value="general">General</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor={`faq-question-${faq.id}`}>Question</Label>
                        <Input
                          id={`faq-question-${faq.id}`}
                          value={faq.question}
                          onChange={(e) => updateFAQ(index, "question", e.target.value)}
                          placeholder="Enter question"
                          className="transition-all duration-200 focus:ring-2 focus:ring-purple-400"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor={`faq-answer-${faq.id}`}>Answer *</Label>
                      <div className="w-full border border-input rounded-md p-0 min-h-[120px] bg-muted/30">
                        <RichTextEditor
                          value={faq.answer}
                          onChange={(value) => updateFAQ(index, "answer", value)}
                          minHeight={220}
                          showBorder={false}
                          enableAI={true}
                          title={`FAQ Answer: ${faq.question || 'Answer'}`}
                          aiContext={`FAQ Question: ${faq.question}`}
                        />
                      </div>
                      {faq.answer && faq.answer.replace(/<[^>]*>/g, '').trim().length < 10 && (
                        <p className="text-sm text-red-600 mt-1">
                          Answer must be at least 10 characters long (excluding HTML tags)
                        </p>
                      )}
                    </div>
                  </div>
              </div>
            );
          })}
        </div>

        {faqs?.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <MessageCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 mb-2">No FAQs added yet</p>
            <p className="text-sm text-gray-400">
              Click "Add FAQ" to start building your FAQ section
            </p>
          </div>
        )}
      </Card>

      {/* Help Section */}
      <Card className="p-6 bg-green-50 border-green-200">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <MessageCircle className="h-4 w-4 text-green-600" />
            </div>
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-green-900 mb-2">
              FAQ Best Practices
            </h4>
            <div className="text-sm text-green-800 space-y-1">
              <p>• <strong>Cover Common Questions:</strong> Address the most frequently asked questions about your program</p>
              <p>• <strong>Be Specific:</strong> Provide detailed, helpful answers that reduce the need for support requests</p>
              <p>• <strong>Use Categories:</strong> Organize FAQs by type (General, Pricing, Schedule, etc.) for better user experience</p>
              <p>• <strong>Keep Updated:</strong> Regularly review and update FAQs based on new questions from students</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
